import React, { useRef, useState } from "react";
import { Colors } from "../../Constants/colors";
import { useDispatch, useSelector } from "react-redux";
import {
  setShowSideMenuBar,
  setSideMenuOption,
} from "../../Redux/Slices/sideMenu.slice";
import { RootState } from "../../Redux/store";
import { useNavigate } from "react-router-dom";
import { isUserRoleValidForNavigation } from "../../roleConfig";
import {
  FONT_SIZE,
  FONT_WEIGHT,
  ALIGN_ITEMS,
  JUSTIFY_CONTENT,
  FLEX_DIRECTION,
} from "../../Constants/Enums/styles.enum";

interface Props {
  title: string;
  onClick?: () => unknown;
  icon: {
    src: string;
    alt: string;
  };
  navigateTo?: string;
  subButtons?: { name: string; onClick?: () => unknown; navigateTo?: string }[];
  isCollapsed?: boolean;
}

const SideMenuBtn: React.FC<Props> = ({
  title,
  onClick,
  icon,
  navigateTo,
  subButtons,
  isCollapsed = false,
}) => {
  const [subButtonClickedIndex, setSubButtonClickedIndex] = useState<
    number | null
  >(null);
  const [isHovered, setIsHovered] = useState(false);
  const [flyoutPosition, setFlyoutPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const itemRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const navigation = useNavigate();

  const buttonClicked = useSelector((state: RootState) => ({
    title: state.sideMenu.title,
    isClicked: state.sideMenu.isClicked,
  }));

  const { role } = useSelector((state: RootState) => state.user);

  let isButtonVisible = false;

  if (navigateTo) {
    isButtonVisible = isUserRoleValidForNavigation({
      path: navigateTo,
      userRole: role,
    });
  } else if (subButtons?.length) {
    subButtons.forEach((button) => {
      if (
        isUserRoleValidForNavigation({
          path: button?.navigateTo,
          userRole: role,
        })
      ) {
        isButtonVisible = true;
        return;
      }
    });
  }

  if (!isButtonVisible) {
    return null;
  }

  const isActive = buttonClicked?.title === title && buttonClicked.isClicked;
  const visibleSubButtons =
    subButtons?.filter((button) =>
      isUserRoleValidForNavigation({
        path: button?.navigateTo,
        userRole: role,
      }),
    ) || [];
  const showFlyout = isCollapsed && isHovered;

  return (
    <div
      ref={itemRef}
      style={{
        width: "100%",
      }}
      onMouseEnter={() => {
        setIsHovered(true);
        if (isCollapsed && itemRef.current) {
          const rect = itemRef.current.getBoundingClientRect();
          const flyoutHeight =
            44 + (subButtons?.length || 0) * 52;
          const maxTop = Math.max(8, window.innerHeight - flyoutHeight - 8);
          setFlyoutPosition({
            top: Math.min(rect.top, maxTop),
            left: rect.right,
          });
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        setFlyoutPosition(null);
      }}
    >
      <div
        style={{
          width: "100%",
          height: "56px",
          display: "flex",
          alignItems: ALIGN_ITEMS.CENTER,
          justifyContent: JUSTIFY_CONTENT.CENTER,
          backgroundColor: isActive
            ? "rgba(158, 7, 7, 0.6)"
            : isHovered
              ? "rgba(158, 7, 7, 0.4)"
              : "transparent",
          borderLeft: isActive
            ? `3px solid ${Colors.TangerineOrange}`
            : "3px solid transparent",
          color: Colors.White,
          cursor: "pointer",
          transition: "all 0.3s ease",
          position: "relative",
        }}
        onClick={() => {
          onClick?.();
          navigateTo && navigation(navigateTo);
          dispatch(setSideMenuOption(title));
          !subButtons && dispatch(setShowSideMenuBar(false));
        }}
        role="button"
        aria-label={title}
        title={isCollapsed ? title : undefined}
        tabIndex={0}
      >
        <div
          style={{
            display: "flex",
            flexDirection: FLEX_DIRECTION.ROW,
            alignItems: ALIGN_ITEMS.CENTER,
            justifyContent: isCollapsed
              ? JUSTIFY_CONTENT.CENTER
              : JUSTIFY_CONTENT.START,
            width: isCollapsed ? "100%" : "85%",
            gap: isCollapsed ? "0px" : "12px",
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: ALIGN_ITEMS.CENTER,
              justifyContent: JUSTIFY_CONTENT.CENTER,
              flexShrink: 0,
            }}
          >
            <img
              src={icon.src}
              alt={icon.alt}
              style={{
                width: "20px",
                height: "20px",
                filter:
                  "brightness(0) saturate(100%) invert(63%) sepia(85%) saturate(1677%) hue-rotate(359deg) brightness(102%) contrast(99%)",
                opacity: isActive ? 1 : 0.9,
                transition: "opacity 0.3s ease",
              }}
            />
          </div>
          {!isCollapsed ? (
            <div
              style={{
                flex: 1,
                fontSize: FONT_SIZE.F16,
                fontWeight: isActive
                  ? FONT_WEIGHT.SEMI_BOLD
                  : FONT_WEIGHT.MEDIUM,
                letterSpacing: "0.3px",
                transition: "font-weight 0.3s ease",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </div>
          ) : null}
          {!isCollapsed && subButtons && subButtons.length > 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: ALIGN_ITEMS.CENTER,
                justifyContent: JUSTIFY_CONTENT.CENTER,
                transition: "transform 0.3s ease",
                transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 8 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.589996 10.59L5.17 6L0.589996 1.41L2 0L8 6L2 12L0.589996 10.59Z"
                  fill={isActive ? Colors.White : Colors.White}
                />
              </svg>
            </div>
          ) : null}
        </div>
      </div>
      {!isCollapsed && subButtons ? (
        <div
          style={{
            maxHeight: isActive ? `${subButtons.length * 52}px` : "0",
            overflow: "hidden",
            transition: "max-height 0.4s ease-in-out",
            backgroundColor: Colors.BurgundyRed,
          }}
        >
          {subButtons.map((button, index) => {
            const isSubButtonVisible = isUserRoleValidForNavigation({
              path: button?.navigateTo,
              userRole: role,
            });

            if (!isSubButtonVisible) {
              return null;
            }

            const isSubActive = subButtonClickedIndex === index;

            return (
              <SubMenuItem
                key={`${title}-${index}`}
                button={button}
                index={index}
                isActive={isSubActive}
                indented
                onClick={() => {
                  setSubButtonClickedIndex(index);
                  button.navigateTo && navigation(button.navigateTo);
                  button.onClick?.();
                  dispatch(setShowSideMenuBar(false));
                }}
              />
            );
          })}
        </div>
      ) : null}
      {showFlyout && flyoutPosition ? (
        <div
          style={{
            position: "fixed",
            top: flyoutPosition.top,
            left: flyoutPosition.left,
            zIndex: 1200,
            minWidth: "180px",
            backgroundColor: Colors.BurgundyRed,
            boxShadow: "4px 4px 16px rgba(0, 0, 0, 0.25)",
            borderRadius: "0 8px 8px 0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              fontSize: FONT_SIZE.F14,
              fontWeight: FONT_WEIGHT.SEMI_BOLD,
              color: Colors.TangerineOrange,
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
          {visibleSubButtons.map((button, index) => {
            const isSubActive = subButtonClickedIndex === index;

            return (
              <SubMenuItem
                key={`${title}-flyout-${index}`}
                button={button}
                index={index}
                isActive={isSubActive}
                onClick={() => {
                  setSubButtonClickedIndex(index);
                  button.navigateTo && navigation(button.navigateTo);
                  button.onClick?.();
                  dispatch(setShowSideMenuBar(false));
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

interface SubMenuItemProps {
  button: { name: string; onClick?: () => unknown; navigateTo?: string };
  index: number;
  isActive: boolean;
  onClick: () => void;
  indented?: boolean;
}

const SubMenuItem: React.FC<SubMenuItemProps> = ({
  button,
  isActive,
  onClick,
  indented = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        width: "100%",
        height: "52px",
        display: "flex",
        alignItems: ALIGN_ITEMS.CENTER,
        justifyContent: JUSTIFY_CONTENT.CENTER,
        backgroundColor: isActive
          ? "rgba(158, 7, 7, 0.5)"
          : isHovered
            ? "rgba(158, 7, 7, 0.3)"
            : "transparent",
        color: Colors.White,
        cursor: "pointer",
        transition: "all 0.2s ease",
        borderLeft: isActive
          ? `2px solid ${Colors.TangerineOrange}`
          : "2px solid transparent",
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      aria-label={button.name}
      tabIndex={0}
    >
      <div
        style={{
          display: "flex",
          flexDirection: FLEX_DIRECTION.ROW,
          alignItems: ALIGN_ITEMS.CENTER,
          width: "85%",
          gap: "12px",
          paddingLeft: indented ? "20px" : "0px",
        }}
      >
        <div
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: isActive ? Colors.White : Colors.Grey,
            flexShrink: 0,
            transition: "background-color 0.2s ease",
          }}
        />
        <div
          style={{
            flex: 1,
            fontSize: FONT_SIZE.F14,
            fontWeight: isActive ? FONT_WEIGHT.MEDIUM : FONT_WEIGHT.REGULAR,
            opacity: isActive ? 1 : 0.9,
            letterSpacing: "0.2px",
            transition: "all 0.2s ease",
          }}
        >
          {button.name}
        </div>
      </div>
    </div>
  );
};

export default SideMenuBtn;
